import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { IActionHandler, OrgInfo, Sort } from "@preveil-api";
import {
  Account, AdminMessages, AdminErrorMessages, AdminSuccessMessages, GetTrustedCommunitiesApiResponse, useAppDispatch, useToggleTrustedCommunityMutation, useGetTrustedCommunitiesMutation, useRemoveFromTrustedCommunityMutation, Message,
  MessageAnchors, MessageHandlerDisplayType, MessageToastTypes, RegexHelper, useAppSelector, SORT_DIRECTION, getOppositeSortDirection, sortStrings
} from "src/common";
import { AlertWithSwitch, PageHeader, CoverTemplate } from "src/components";
import { AddTrustedAddressModal, ListView, NoAddressesView, Toolbar } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
};

// Description: Trusted Community is a feature in a PreVeil organization that restricts communication to a whitelisted set of trusted domains and/or user email addresses.
// Link: https://preveil.atlassian.net/wiki/spaces/ESD/pages/1481834516/Admin+Console+-+Trusted+Community
function TrustedCommunityComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [show_modal, setShowModal] = useState<boolean>(false);
  const [address_list, setAddressList] = useState<string[]>([]);
  const [filtered_list, setFilteredList] = useState<string[]>([]);
  const [is_trusted_community_active, setTrustedCommunityActiveState] = useState<boolean | undefined>(false);
  const [filter_param, setFilterParam] = useState("");
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<Sort<string>>({ field: "address", direction: "asc" });
  const [toggleTrustedCommunity] = useToggleTrustedCommunityMutation();
  const [getTrustedCommunities] = useGetTrustedCommunitiesMutation();
  const [removeFromTrustedCommunity] = useRemoveFromTrustedCommunityMutation();

  useEffect(() => {
    getWhitelistAddresses();
  }, []);

  // Description: Fetch the list of addresses/domains from server
  async function getWhitelistAddresses() {
    const { user_id } = current_account;
    setIsLoading(true);
    await getTrustedCommunities({ userId: user_id, orgId: org_info.org_id })
      .unwrap()
      .then((payload: GetTrustedCommunitiesApiResponse) => {
        if (payload?.domains || payload?.users) {
          const { users, domains, is_active } = payload;
          const _users = !!users ? users : [];
          const _domains = !!domains ? domains : [];
          const address_list: string[] = [..._users, ..._domains];
          const sorted_addresses = sortStrings(address_list, { field: sort.field, direction: sort.direction });
          setIsLoading(false);
          setAddressList(sorted_addresses);
          setFilteredList(sorted_addresses);
          setTrustedCommunityActiveState(is_active);
        }
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_fetching_trusted_community,
          stack: error
        };
        setIsLoading(false);
        dispatchPageError(params);
      });
  }

  // Description: Handles request to remove selected addresses/domains.
  async function removeFromWhitelistAddresses(addresses: string[]) {
    const { user_id } = current_account;
    const collection = _.isArray(addresses) ? addresses : selected;
    const domains = collection.filter((element) => RegexHelper.testDomain(element));
    const users = collection.filter((element) => RegexHelper.testEmailAddress(element));
    setFilterParam("");
    await removeFromTrustedCommunity({ userId: user_id, orgId: org_info.org_id, users, domains })
      .unwrap()
      .then(() => {
        getWhitelistAddresses();
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_delete_trusted_community_addresses)));
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_removing_trusted_community,
          stack: error
        };
        dispatchPageError(params);
      });
  };

  // Description: Handles request to activate/deactivate trusted community.
  async function toggleTrustedCommunityRequest() {
    const { user_id } = current_account;
    await toggleTrustedCommunity({ userId: user_id, orgId: org_info.org_id, setActive: !is_trusted_community_active })
      .unwrap()
      .then(() => {
        const message = is_trusted_community_active ? AdminSuccessMessages.success_disable_trusted_community : AdminSuccessMessages.success_enable_trusted_community;
        setTrustedCommunityActiveState(!is_trusted_community_active);
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((error) => {
        const failed_toggle_state = is_trusted_community_active ? "disabling" : "enabling";
        const params = {
          message: AdminErrorMessages.error_toggle_trusted_community.replace(MessageAnchors.message_content, failed_toggle_state),
          stack: error
        };
        dispatchPageError(params);
      });
  };

  // Description: dispatch any error found in the page.
  function dispatchPageError(params: { message: string, stack?: any }) {
    dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
  }

  // Description: function will handle the confirmation from the user
  // if they want to change the status of the trusted community (active/no active).
  function confirmToggleTrustedCommunity() {
    const dialog_message = is_trusted_community_active ? AdminMessages.disable_community : AdminMessages.enable_community;
    const confirmation_dialog = new Message(
      dialog_message.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      dialog_message.title,
      {
        label: "Yes",
        data: true,
        action: () => toggleTrustedCommunityRequest()
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  };

  // Description: Handles the confirmation dialog, warning user if they want to
  // delete the selected addresses/domains.
  function confirmDeleteAddressesDialog(addresses: string[]) {
    const confirmation_dialog = new Message(
      AdminMessages.confirm_delete_address.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_delete_address.title,
      {
        label: "Yes",
        data: true,
        action: () => removeFromWhitelistAddresses(addresses)
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Handle all children component actions.
  const TrustedCommunityRequests = {
    handleSetSort: (params: "address") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortStrings(filtered_list, { field: params, direction });
      setSort({ field: params, direction });
      setFilteredList(sorted_requests);
    },
    handleGetRefreshRequest: () => {
      getWhitelistAddresses();
    },
    handleDelete: (addresses: string[]) => {
      confirmDeleteAddressesDialog(addresses);
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      dispatchPageError(params);
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = address_list.filter((address: string) => {
          return address.toLowerCase().includes(param.toLowerCase());
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(address_list);
      }
    },
    handleSelect: (params: { address?: string, selected: boolean }) => {
      let new_selected = selected.slice();
      if (!!params.address) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === params.address)) &&
            new_selected.push(params.address);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === params.address));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        new_selected = params.selected ? filtered_list : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handleShowModal: () => {
      setShowModal(!show_modal);
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((TrustedCommunityRequests as any)[callback] instanceof Function) {
      (TrustedCommunityRequests as any)[callback](action_obj.params);
    } else {
      const { params } = action_obj;
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    }
  }

  return <CoverTemplate className="admin-component">
    <PageHeader>
      <h1>Trusted Community</h1>
    </PageHeader>
    <Card className="card-section h-100">
      <AlertWithSwitch
        popover={false}
        toggleState={is_trusted_community_active}
        handleChange={confirmToggleTrustedCommunity}
      >
        <>Your organization's trusted community is turned {is_trusted_community_active ? "on" : "off"}</>
      </AlertWithSwitch>
      <Toolbar is_loading={is_loading} address_list={address_list} handleAction={handlePageActions} filter_param={filter_param}/>
      {address_list.length === 0 && !is_loading ? 
        <NoAddressesView handleAction={handlePageActions}/> : 
        <div id="outer-scope" className="cover-content">
          <ListView address_list={filtered_list} is_loading={is_loading} selected={selected} sort={sort} handleAction={handlePageActions}  />
        </div>}
      <AddTrustedAddressModal
        org_info={org_info}
        current_account={current_account}
        show_modal={show_modal}
        handleAction={handlePageActions}
        address_list={address_list}
      />
    </Card>
  </CoverTemplate>;
}

export default React.memo(TrustedCommunityComponent);
